import { useEffect } from "react";
import { buildCanonical, buildTitle, defaultSeo } from "@/config/seo.config";
import { siteConfig } from "@/config/site.config";

export interface SeoProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
  type?: "website" | "article";
}

/** Upserts a `<meta>` tag by name or property. */
function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

/**
 * Declarative document head management without an extra dependency.
 *
 * Rendered near the top of every page. Sets title, description, canonical URL,
 * Open Graph + Twitter cards and robots directives. Structured for a future
 * swap to a CMS-driven / i18n metadata source.
 */
export function Seo({
  title,
  description = defaultSeo.description,
  path = "/",
  image = defaultSeo.image,
  noindex = false,
  type = "website",
}: SeoProps) {
  useEffect(() => {
    const fullTitle = buildTitle(title);
    const canonical = buildCanonical(path);
    const ogImage = image?.startsWith("http")
      ? image
      : `${siteConfig.url}${image}`;

    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta(
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow",
    );

    // Open Graph
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:site_name", siteConfig.name);
    if (ogImage) setMeta("property", "og:image", ogImage);

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    if (ogImage) setMeta("name", "twitter:image", ogImage);

    setCanonical(canonical);
  }, [title, description, path, image, noindex, type]);

  return null;
}
